import { GET_USER } from '@/hooks/query/current-user-cache';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { EffectiveSessionTimeoutSource } from '@heidi/schema/ml-scribe';
import type { EffectiveSessionTimeout, UserResponse } from '@heidi/workspace/contracts';
import { useCurrentUserQueryResult } from '@heidi/workspace/data';
import { getDefaultStore, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { resolveEffectiveSessionTimeout } from './session-timeout-resolve';
import { userSessionTimeoutOverrideAtom } from './session-timeout-storage';

export { resolveEffectiveSessionTimeout } from './session-timeout-resolve';
export {
  USER_SESSION_TIMEOUT_STORAGE_KEY,
  userSessionTimeoutOverrideAtom,
} from './session-timeout-storage';

/**
 * Writes a just-saved timeout into the `GET /user` cache and the override atom
 * so `AutoLogout` picks it up without waiting for a refetch.
 *
 * `cancelQueries` is AWAITED: an in-flight `GET /user` that resolves after
 * `setQueryData` would overwrite the patch with the pre-save body, which is the
 * race this exists to close. Fire-and-forget left it open.
 */
export async function patchUserSessionTimeoutCache(
  sessionTimeout: EffectiveSessionTimeout | null
) {
  await reactQueryClient.cancelQueries({ queryKey: [GET_USER] });
  reactQueryClient.setQueryData<UserResponse>([GET_USER], (previous) => {
    if (!previous) return previous;
    return { ...previous, session_timeout: sessionTimeout };
  });
  getDefaultStore().set(userSessionTimeoutOverrideAtom, sessionTimeout);
}

/**
 * The timeout `AutoLogout` enforces: the server's value when it has one, the
 * user's last saved value when it does not.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * DELIBERATE: the client override outranks a server `OPT_IN_AVAILABLE`.
 *
 * `GET /user` does not materialise the value a successful PATCH just stored —
 * it answers `{ minutes: null, source: OPT_IN_AVAILABLE }`, the same payload it
 * sends a user who has never opted in. The two cases are indistinguishable on
 * the wire, so the client cannot both bridge the bug and honour a genuine
 * withdrawal. We bridge the bug: dropping the override instead would silently
 * stop enforcing a med-device control (SWR-84 / SWR-174) the user explicitly
 * turned on, which is the worse of the two failures.
 *
 * What this costs, so it is not discovered later:
 * - A timeout withdrawn by the backend while `OPT_IN_AVAILABLE` is returned
 *   keeps being enforced on this device until the user disables it themselves.
 * - iOS and Android read `GET /user` directly, so they will not enforce a
 *   timeout this client still does. Per-device divergence on a compliance
 *   control, resolvable only by the backend materialising the field.
 *
 * The remote kill path still works: a cohort rollback returns
 * `session_timeout: null`, and the effect below clears the override on null, so
 * disabling the rollout does stop enforcement fleet-wide. Only the narrower
 * `OPT_IN_AVAILABLE` transition is held.
 * ───────────────────────────────────────────────────────────────────────────
 */
export function useEffectiveSessionTimeout() {
  // Selected down to `session_timeout` on purpose. This hook is read from the
  // always-mounted `GlobalModalsWrapper`; subscribing to the whole user object
  // re-ran the effect below — and rewrote localStorage — on every refetch that
  // touched any unrelated field.
  const { data: serverSessionTimeout, isSuccess } = useCurrentUserQueryResult(
    (data) => data.session_timeout ?? null
  );
  const override = useAtomValue(userSessionTimeoutOverrideAtom);
  const setOverride = useSetAtom(userSessionTimeoutOverrideAtom);

  useEffect(() => {
    if (!isSuccess) return;

    if (serverSessionTimeout == null) {
      setOverride(null);
      return;
    }
    if (
      typeof serverSessionTimeout.minutes === 'number' &&
      serverSessionTimeout.minutes > 0
    ) {
      setOverride(serverSessionTimeout);
      return;
    }
    if (serverSessionTimeout.source === EffectiveSessionTimeoutSource.USER_DISABLED) {
      setOverride(serverSessionTimeout);
    }
    // OPT_IN_AVAILABLE falls through: see the precedence note above — it is the
    // same payload the BE sends after a successful PATCH, so clearing here would
    // drop the opt-in it is meant to preserve.
  }, [isSuccess, serverSessionTimeout, setOverride]);

  return resolveEffectiveSessionTimeout(serverSessionTimeout, override);
}
